import React, { useEffect, useMemo, useState } from "react";
import { Popover } from "antd";
import { useTranslation } from "react-i18next";
import { DATA_CHANNELS } from "shared/config/setting";
import { useEventBus } from "shared/services/eventBus";
import { ROBOT_EVENTS, getRobotEvent } from "shared/services/eventBus/eventTypes";
import { useConnectionManager } from "features/robot-teleop/contexts/RobotConnectionManagerContext";
import { usePeerConnection } from "features/robot-teleop/hooks/usePeerConnection";
import { usePeerDataChannel } from "features/robot-teleop/hooks/usePeerDataChannel";
import { usePeerStats } from "features/robot-teleop/hooks/usePeerStats";
import { PEER_STATES } from "features/robot-teleop/webrtc/peerStates";
import Sparkline from "./Sparkline";
import PeerStatsDetails from "./PeerStatsDetails";
import { dotStatus, gradeColor, gradeText, GRADE_LETTER, fmtMs, fmtPct } from "./linkPresentation";

const SPARK_SAMPLES = 30;

/**
 * Summary of the robot's peers (connected / failed / stalled counts), refreshed
 * on any peer state or media-health change.
 */
const usePeerSummary = () => {
  const { manager, peerIds } = useConnectionManager();
  const eventBus = useEventBus();
  const summarize = () => {
    const peers = manager ? manager.getAllPeers() : [];
    return {
      total: peers.length,
      connected: peers.filter((p) => p.state === PEER_STATES.CONNECTED).length,
      failed: peers.filter((p) => p.state === PEER_STATES.FAILED).length,
      stalled: peers.filter((p) => p.state === PEER_STATES.CONNECTED && p.hasCamera
        && (p.videoHealth === "stalled" || p.videoHealth === "no-track")).length,
    };
  };
  const [summary, setSummary] = useState(summarize);
  useEffect(() => {
    setSummary(summarize());
    const refresh = () => setSummary(summarize());
    const subs = [];
    peerIds.forEach((id) => {
      subs.push(eventBus.subscribe(getRobotEvent(id, ROBOT_EVENTS.CONNECTION_STATE), refresh));
      subs.push(eventBus.subscribe(getRobotEvent(id, ROBOT_EVENTS.MEDIA_STATE_CHANGED), refresh));
    });
    return () => subs.forEach((unsub) => { if (typeof unsub === "function") unsub(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager, peerIds, eventBus]);
  return summary;
};

/**
 * ControlLinkIndicator — header indicator for the CONTROL peer (the one the
 * commands go through): dot + state, data-channel readiness, link quality
 * (grade A–D from RTT / jitter / loss, the numbers, an RTT sparkline of the
 * last 30 s) and how many of the robot's peers are connected. Click opens the
 * full live metric table of the control peer.
 */
const ControlLinkIndicator = () => {
  const { t } = useTranslation();
  const { controlPeerId } = useConnectionManager();
  const { state, connectionState, retryCount, cameras } = usePeerConnection(controlPeerId);
  const { isOpen: channelOpen } = usePeerDataChannel(controlPeerId, DATA_CHANNELS.CHAT);
  const { sample, history, health, grade } = usePeerStats(controlPeerId, { historySize: SPARK_SAMPLES });
  const { connected, total, failed, stalled } = usePeerSummary();

  let dot = dotStatus({ state, connectionState, health, grade, retryCount, expectsVideo: cameras.length > 0 }, t);
  if (state === PEER_STATES.CONNECTED && !channelOpen) {
    dot = { cls: "degraded", text: t("teleop.peer.state.dataChannelClosed"), alert: true };
  }
  const isConnected = state === PEER_STATES.CONNECTED;
  const rtt = sample?.rttMs ?? null;
  const jitter = sample?.video?.jitterMs ?? sample?.audio?.jitterMs ?? null;
  const loss = sample?.video?.packetLossPct ?? sample?.audio?.packetLossPct ?? null;
  const rttSeries = useMemo(() => history.map((s) => s?.rttMs ?? null), [history]);
  const qualityText = grade
    ? t("teleop.peer.quality.label", { grade: GRADE_LETTER[grade], text: gradeText(grade, t) })
    : t("teleop.peer.quality.unknown");

  const partial = total > 0 && connected < total;
  const troubled = failed > 0 || stalled > 0;
  const peersText = [
    t("teleop.peer.header.peersConnected", { connected, total }),
    failed ? t("teleop.peer.header.peersFailed", { n: failed }) : null,
    stalled ? t("teleop.peer.header.peersWithoutVideo", { n: stalled }) : null,
  ].filter(Boolean).join(", ");
  const title = [
    t("teleop.peer.header.controlPeer", { peerId: controlPeerId || "–", status: dot.text }),
    channelOpen ? t("teleop.peer.header.dataChannelOpen") : null,
    isConnected ? qualityText : null,
    rtt != null ? t("teleop.peer.metric.rttText", { value: fmtMs(rtt) }) : null,
    jitter != null ? t("teleop.peer.metric.jitterText", { value: fmtMs(jitter, 1) }) : null,
    loss != null ? t("teleop.peer.metric.lossText", { value: fmtPct(loss) }) : null,
    peersText,
    controlPeerId ? t("teleop.peer.badge.clickForDetails") : null,
  ].filter(Boolean).join(" · ");
  const peersTag = [
    t("teleop.peer.header.peersTag", { connected, total }),
    stalled ? t("teleop.peer.header.noVideoTag", { n: stalled }) : null,
    failed ? t("teleop.peer.header.failedTag", { n: failed }) : null,
  ].filter(Boolean).join(" · ");

  const indicator = (
    <div
      className="control-link-indicator"
      title={title}
      role="button"
      tabIndex={0}
      data-peer-id={controlPeerId || undefined}
    >
      <span className={`dot ${dot.cls}`} />
      <span className="status-text">{dot.text}</span>
      {isConnected && grade && (
        <span className={`grade ${grade}`} aria-label={qualityText}>{GRADE_LETTER[grade]}</span>
      )}
      {isConnected && rtt != null && (
        <span className="metric rtt" title={t("teleop.peer.metric.rtt")}><b>{fmtMs(rtt)}</b> {t("teleop.peer.metric.ms")}</span>
      )}
      {isConnected && jitter != null && (
        <span className="metric jitter" title={t("teleop.peer.metric.jitter")}>⌇ <b>{fmtMs(jitter, 1)}</b> {t("teleop.peer.metric.ms")}</span>
      )}
      {isConnected && loss != null && loss > 0 && (
        <span className={`metric loss ${loss >= 3 ? "bad" : ""}`} title={t("teleop.peer.metric.loss")}>
          <b>{fmtPct(loss)}</b> {t("teleop.peer.metric.lossUnit")}
        </span>
      )}
      {isConnected && (
        <Sparkline
          values={rttSeries}
          width={56}
          height={14}
          max={600}
          warnAt={150}
          lastColor={gradeColor(grade)}
          title={t("teleop.peer.metric.rttSpark")}
        />
      )}
      {total > 0 && (
        <span className={`peers-tag ${troubled ? "alert" : partial ? "partial" : ""}`}>{peersTag}</span>
      )}
    </div>
  );

  if (!controlPeerId) return indicator;
  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      destroyOnHidden
      content={<PeerStatsDetails peerId={controlPeerId} />}
      title={t("teleop.peer.header.detailsTitle", { peerId: controlPeerId })}
    >
      {indicator}
    </Popover>
  );
};

export default React.memo(ControlLinkIndicator);
